import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../auth/session.js";
import { useFeatures } from "../features.js";
import { LEGAL } from "../legal/company.js";

/** Header and footer shared by the public pages: home, pricing and the legal pages. */
export function PublicLayout({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const features = useFeatures();
  const nextLocale = i18n.language === "tr" ? "en" : "tr";
  const footer: Array<[string, string]> = [
    ["/pricing", t("public.nav.pricing")],
    ["/legal/terms", t("public.legal.terms")],
    ["/legal/privacy", t("public.legal.privacy")],
    ["/legal/refunds", t("public.legal.refunds")],
    ["/legal/acceptable-use", t("public.legal.acceptableUse")],
  ];
  return (
    <div className="flex min-h-full flex-col">
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 py-4">
        <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight text-ink">
          <span className="flex size-8 items-center justify-center rounded-lg bg-accent text-sm font-bold text-white">
            W
          </span>
          {t("app.name")}
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link
            to="/pricing"
            className="text-ink-2 underline-offset-2 hover:underline"
            activeProps={{ className: "text-ink font-medium" }}
          >
            {t("public.nav.pricing")}
          </Link>
          <button
            type="button"
            className="hidden text-ink-3 underline-offset-2 hover:text-ink hover:underline sm:block"
            onClick={() => void i18n.changeLanguage(nextLocale)}
          >
            {nextLocale.toUpperCase()}
          </button>
          {session.data ? (
            <Link to="/mail" className="text-accent underline-offset-2 hover:underline">
              {t("store.openWorkspace")}
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-ink-2 underline-offset-2 hover:underline">
                {t("auth.login.submit")}
              </Link>
              {/* Hidden until the features answer arrives, so neither label flashes. */}
              <Link
                to="/signup"
                className={`rounded-full bg-accent px-4 py-2 font-medium text-white hover:shadow-1 ${features.loaded ? "" : "invisible"}`}
              >
                {features.signup ? t("public.nav.startTrial") : t("public.nav.comingSoon")}
              </Link>
            </>
          )}
        </nav>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-rule">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 px-4 py-6 text-sm text-ink-3 sm:flex-row sm:items-center sm:justify-between">
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            {footer.map(([to, label]) => (
              <Link key={to} to={to} className="hover:text-ink hover:underline">
                {label}
              </Link>
            ))}
            <a href={`mailto:${LEGAL.supportEmail}`} className="hover:text-ink hover:underline">
              {t("public.contact")}
            </a>
          </nav>
          <span>
            © {new Date().getFullYear()} {LEGAL.company}
          </span>
        </div>
      </footer>
    </div>
  );
}
